import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAvatarUrl } from '../api/avatars';
import { useSession } from '../session/useSession';
import { Icon } from './Icon';

/**
 * Les commandes réutilisables de l'interface : bouton-icône avec infobulle,
 * menu d'actions « Plus », boîte de confirmation.
 *
 * L'INFOBULLE N'EST PAS LE NOM ACCESSIBLE. Elle est dessinée en CSS à partir de
 * `data-tip`, au survol ET au focus clavier ; le nom que lit un lecteur d'écran
 * est `aria-label`. Les deux reçoivent le même texte, une seule fois écrit ici.
 * Sur un écran tactile il n'y a pas de survol : c'est pourquoi la fiche et le
 * téléphone n'utilisent PAS d'icônes seules (option B et C retenues).
 */

export function IconButton({
    icon,
    label,
    onClick,
    tone = 'default',
    disabled = false,
}: {
    readonly icon: string;
    readonly label: string;
    onClick(): void;
    readonly tone?: 'default' | 'danger';
    readonly disabled?: boolean;
}): ReactNode {
    return (
        <button
            className={`icon-button${tone === 'danger' ? ' icon-button--danger' : ''}`}
            type="button"
            aria-label={label}
            data-tip={label}
            disabled={disabled}
            onClick={onClick}
        >
            <Icon name={icon} size={18} />
        </button>
    );
}

export function IconLink({ icon, label, to }: { readonly icon: string; readonly label: string; readonly to: string }): ReactNode {
    return (
        <Link className="icon-button" to={to} aria-label={label} data-tip={label}>
            <Icon name={icon} size={18} />
        </Link>
    );
}

export interface MenuAction {
    readonly key: string;
    readonly label: string;
    readonly icon: string;
    readonly tone?: 'default' | 'danger';
    onSelect(): void;
}

/**
 * Le menu « Plus » (option C) : un bouton qui ouvre une liste d'actions avec
 * leur libellé complet. Se ferme au clic dehors, sur Échap (le focus revient au
 * bouton) et après un choix.
 */
export function ActionMenu({ actions, label }: { readonly actions: readonly MenuAction[]; readonly label?: string }): ReactNode {
    const { t } = useSession();
    const [open, setOpen] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const id = useId();

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const outside = (event: PointerEvent): void => {
            if (root.current !== null && !root.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const escape = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setOpen(false);
                trigger.current?.focus();
            }
        };

        document.addEventListener('pointerdown', outside);
        document.addEventListener('keydown', escape);
        root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();

        return () => {
            document.removeEventListener('pointerdown', outside);
            document.removeEventListener('keydown', escape);
        };
    }, [open]);

    if (actions.length === 0) {
        return null;
    }

    const name = label ?? t('common.moreActions');

    return (
        <div className="action-menu" ref={root}>
            <button
                ref={trigger}
                className={`icon-button${open ? ' icon-button--active' : ''}`}
                type="button"
                aria-label={name}
                data-tip={open ? undefined : name}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={id}
                onClick={() => setOpen((value) => !value)}
            >
                <Icon name="more" size={18} />
            </button>
            {open ? (
                <div className="action-menu__list" role="menu" id={id}>
                    {actions.map((action) => (
                        <button
                            key={action.key}
                            className={`action-menu__item${action.tone === 'danger' ? ' action-menu__item--danger' : ''}`}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpen(false);
                                action.onSelect();
                            }}
                        >
                            <Icon name={action.icon} size={18} />
                            {action.label}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/**
 * Une confirmation avant une action qui se voit chez quelqu'un d'autre
 * (désactiver, envoyer un courriel). `<dialog>` natif en mode modal : le
 * navigateur gère le piège du focus, Échap et l'arrière-plan inerte.
 */
export function ConfirmDialog({
    open,
    title,
    text,
    confirmLabel,
    danger = false,
    pending = false,
    onConfirm,
    onCancel,
}: {
    readonly open: boolean;
    readonly title: string;
    readonly text: string;
    readonly confirmLabel: string;
    readonly danger?: boolean;
    readonly pending?: boolean;
    onConfirm(): void;
    onCancel(): void;
}): ReactNode {
    const { t } = useSession();
    const ref = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = ref.current;

        if (dialog === null) {
            return;
        }

        if (open && !dialog.open) {
            dialog.showModal();
        } else if (!open && dialog.open) {
            dialog.close();
        }
    }, [open]);

    return (
        <dialog
            ref={ref}
            className="dialog"
            aria-labelledby="dialog-title"
            onCancel={(event) => {
                event.preventDefault();
                onCancel();
            }}
        >
            <h2 className="dialog__title" id="dialog-title">
                {title}
            </h2>
            <p className="dialog__text">{text}</p>
            <div className="dialog__actions">
                <button className="button button--ghost" type="button" onClick={onCancel}>
                    {t('common.cancel')}
                </button>
                <button className={danger ? 'button button--danger' : 'button'} type="button" disabled={pending} onClick={onConfirm}>
                    {pending ? t('common.saving') : confirmLabel}
                </button>
            </div>
        </dialog>
    );
}

/** Les initiales d'un nom affiché, pour la pastille d'un compte. */
export function initials(name: string): string {
    // La première LETTRE de chaque mot : « Briand Kpanou (LENSOM) » donne BL, pas B(.
    const letters = name
        .trim()
        .split(/\s+/)
        .map((part) => part.match(/\p{L}/u)?.[0])
        .filter((letter): letter is string => letter !== undefined);
    const first = letters[0] ?? '?';
    const last = letters.length > 1 ? (letters[letters.length - 1] ?? '') : '';

    return `${first}${last}`.toUpperCase();
}

/**
 * La pastille d'un compte : sa photo s'il en a une, ses initiales sinon — et
 * pendant le chargement, pour que rien ne saute.
 *
 * Toujours décorative : le nom est écrit à côté, un lecteur d'écran n'a pas à
 * l'entendre deux fois.
 */
export function Avatar({
    name,
    size = 'md',
    accountId,
    version,
}: {
    readonly name: string;
    readonly size?: 'md' | 'lg' | 'xl';
    readonly accountId?: string;
    readonly version?: string | null;
}): ReactNode {
    const url = useAvatarUrl(accountId, version);

    return (
        <span className={`avatar avatar--${size}`} aria-hidden="true">
            {url === null ? initials(name) : <img className="avatar__image" src={url} alt="" />}
        </span>
    );
}
